import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabaseClient";
import {
    Box, Typography, Stack, Button, IconButton, TextField, InputAdornment,
    Avatar, Skeleton, Pagination, useMediaQuery,
} from "@mui/material";
import { Search, Close } from "@mui/icons-material";
import { styled } from "@mui/material/styles";

/* ── Design tokens (mirrors CPTab) ── */
const C = {
    indigo: "#4338ca", indigo2: "#6366f1", indigoSoft: "#eef2ff", indigoBd: "#c7d2fe",
    green: "#16a34a", greenSoft: "#f0fdf4", greenBd: "#bbf7d0",
    red: "#dc2626",
    page: "#f8fafc", white: "#ffffff", border: "#e2e8f0", border2: "#f1f5f9",
    txt1: "#0f172a", txt2: "#334155", txt3: "#64748b", txt4: "#94a3b8",
    sans: "'Geist', system-ui, sans-serif", mono: "'Geist Mono', monospace",
};

const PAGE_SIZE = 20;

const AV_COLORS = ["#6366f1","#0ea5e9","#8b5cf6","#ec4899","#f59e0b","#22c55e","#ef4444"];
const avColor = n => {
    if (!n) return C.txt4;
    let h = 0;
    for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) & 0xffffffff;
    return AV_COLORS[Math.abs(h) % AV_COLORS.length];
};
const initials = n => {
    if (!n) return "?";
    const p = n.trim().split(" ");
    return (p.length >= 2 ? p[0][0] + p[1][0] : p[0].slice(0, 2)).toUpperCase();
};

/* ── Styled ── */
const SearchBox = styled(TextField)({
    "& .MuiOutlinedInput-root": {
        background: C.white,
        borderRadius: 8,
        fontSize: 13.5,
        "& fieldset": { borderColor: C.border },
    },
});

const CallBtn = styled(Button)({
    borderRadius: 6,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 11,
    padding: "3px 10px",
    background: C.greenSoft,
    color: C.green,
    border: `1px solid ${C.greenBd}`,
    "&:hover": { background: C.green, color: "#fff" },
    "&:disabled": { opacity: 0.5 },
});

/* ── Card (mirrors CPCard) ── */
function FOSCard({ fos, onCall }) {
    const name = fos.name || "Unknown";
    const hasNumber = !!fos.contactNumber;

    return (
        <Box
            sx={{
                background: C.white,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                p: 1.2,
                mb: 1,
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 34, height: 34, background: avColor(name), fontSize: 12 }}>
                        {initials(name)}
                    </Avatar>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: 13.5,
                            color: C.txt1,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {name}
                    </Typography>
                </Stack>
                <CallBtn
                    disabled={!hasNumber}
                    onClick={() => hasNumber && onCall(fos)}
                >
                    {hasNumber ? "Call" : "No #"}
                </CallBtn>
            </Stack>
        </Box>
    );
}

/* ── FOSTab ── */
export default function FOSTab({ onCallNow }) {
    const [fosList, setFosList] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");

    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const fetchFOS = useCallback(async (pg, sv) => {
        setLoading(true);
        try {
            let q = supabase
                .from("FOS")
                .select("fosId, name, contactNumber", { count: "exact" })
                .eq("approvalStatus", "approved")
                .eq("isDisabled", false)
                .range((pg - 1) * PAGE_SIZE, pg * PAGE_SIZE - 1);

            if (sv) q = q.ilike("name", `%${sv}%`);

            const { data, count } = await q;
            setFosList(data || []);
            setTotal(count || 0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchFOS(1, search); }, [search, fetchFOS]);

    const handleCall = fos => {
        const name = fos.name || "Unknown";
        const contactNumber = fos.contactNumber?.toString() || "";
        onCallNow?.({}, { name, contactNumber });
    };

    return (
        <Box>
            {/* SEARCH */}
            <SearchBox
                size="small"
                placeholder="Search by name…"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search sx={{ fontSize: 15, color: C.txt4 }} />
                        </InputAdornment>
                    ),
                    endAdornment: searchInput && (
                        <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSearchInput("")}>
                                <Close sx={{ fontSize: 13, color: C.txt4 }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            {/* LOADING */}
            {loading && (
                <Stack spacing={1}>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: 2 }} />
                    ))}
                </Stack>
            )}

            {/* LIST */}
            {!loading && fosList.map(fos => (
                <FOSCard key={fos.fosId} fos={fos} onCall={handleCall} />
            ))}

            {/* EMPTY */}
            {!loading && fosList.length === 0 && (
                <Box sx={{ py: 7, textAlign: "center" }}>
                    <Typography sx={{ fontSize: 13.5, color: C.txt3, fontFamily: C.sans }}>
                        {search ? "No FOS match your search." : "No approved FOS found."}
                    </Typography>
                </Box>
            )}

            {/* PAGINATION */}
            {Math.ceil(total / PAGE_SIZE) > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Pagination
                        count={Math.ceil(total / PAGE_SIZE)}
                        page={page}
                        onChange={(_, p) => { setPage(p); fetchFOS(p, search); }}
                        shape="rounded"
                        size="small"
                        sx={{ "& .Mui-selected": { background: `${C.green} !important`, color: "#fff" } }}
                    />
                </Box>
            )}
        </Box>
    );
}
